import { useCallback, useMemo } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support/trello';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import {
  type CrossFlowForAdSequencerInternalExperimentVariations,
  useCrossFlowForAdSequencerInternalExperiment,
} from './experimental/useCrossFlowForAdSequencerInternalExperiment';
import {
  type CrossFlowForSporkFreePpmExperimentVariations,
  useCrossFlowForFreePpmSpork,
} from './experimental/useCrossFlowForFreePpmSpork';
import {
  type CrossFlowForSporkMultiplayerExperimentVariations,
  useCrossFlowForMultiplayerSpork,
} from './experimental/useCrossFlowForMultiplayerSpork';
import {
  type NewUserNudgesExperimentVariations,
  useCrossFlowForNewUserNudges,
} from './experimental/useCrossFlowForNewUserNudges';
import {
  type CrossFlowForSporkNewUsersExperimentVariations,
  useCrossFlowForNewUsersSpork,
} from './experimental/useCrossFlowForNewUsersSpork';
import {
  type NonPpmFreeMultiplayerExperimentVariations,
  useCrossFlowForNonPpmFreeMultiplayer,
} from './experimental/useCrossFlowForNonPpmFreeMultiplayer';
import {
  type NonPpmFreeSinglePlayerExperimentVariations,
  useCrossFlowForNonPpmFreeSinglePlayerExperiment,
} from './experimental/useCrossFlowForNonPpmFreeSinglePlayerExperiment';
import {
  type CrossFlowForSporkPaidUsersExperimentVariations,
  useCrossFlowForPaidUsersSpork,
} from './experimental/useCrossFlowForPaidUsersSpork';
import type { CrossFlowForPaidWorkspacesExperimentVariations } from './experimental/useCrossFlowForPaidWorkspaces';
import { useCrossFlowForPaidWorkspaces } from './experimental/useCrossFlowForPaidWorkspaces';
import {
  type CrossFlowForPpmFreeWorkspacesLessThanTenUsersExperimentVariations,
  useCrossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment,
} from './experimental/useCrossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment';
import {
  type CrossFlowForSporkSinglePlayerExperimentVariations,
  useCrossFlowForSinglePlayerSpork,
} from './experimental/useCrossFlowForSinglePlayerSpork';
import {
  useCrossFlowForWelcomeScreenTaskSelection,
  type WelcomeScreenTaskSelectionExperimentVariations,
} from './experimental/useCrossFlowForWelcomeScreenTaskSelection';
import { useCrossFlowMemberFragment } from './CrossFlowMemberFragment.generated';
import { type TrelloCrossFlow, trelloCrossFlowOpen } from './TrelloCrossFlow';

export interface TrelloCrossFlowOptions {
  workspaceId?: string;
  boardId?: string;
  isNewTrelloUser?: boolean;
}

/**
 * Union type of all possible variations for crossFlow experiments
 */
export type ExperimentVariations =
  | TrelloCrossFlow<NonPpmFreeSinglePlayerExperimentVariations>
  | TrelloCrossFlow<NonPpmFreeMultiplayerExperimentVariations>
  | TrelloCrossFlow<NewUserNudgesExperimentVariations>
  | TrelloCrossFlow<CrossFlowForPaidWorkspacesExperimentVariations>
  | TrelloCrossFlow<CrossFlowForPpmFreeWorkspacesLessThanTenUsersExperimentVariations>
  | TrelloCrossFlow<WelcomeScreenTaskSelectionExperimentVariations>
  | TrelloCrossFlow<CrossFlowForSporkPaidUsersExperimentVariations>
  | TrelloCrossFlow<CrossFlowForAdSequencerInternalExperimentVariations>
  | TrelloCrossFlow<CrossFlowForSporkSinglePlayerExperimentVariations>
  | TrelloCrossFlow<CrossFlowForSporkMultiplayerExperimentVariations>
  | TrelloCrossFlow<CrossFlowForSporkFreePpmExperimentVariations>
  | TrelloCrossFlow<CrossFlowForSporkNewUsersExperimentVariations>
  | TrelloCrossFlow<WelcomeScreenTaskSelectionExperimentVariations>;

export const useTrelloCrossFlow = (
  source: TouchpointSourceType,
  { workspaceId, boardId, isNewTrelloUser }: TrelloCrossFlowOptions = {},
): ExperimentVariations => {
  const crossFlow = useCrossFlow();

  const [hasValidAaSession] = useHasValidAaSession();

  const analyticsContainers = useMemo(
    () => ({
      organization: {
        id: workspaceId,
      },
      board: {
        id: boardId,
      },
    }),
    [workspaceId, boardId],
  );

  const { value: doSendOperationalEvent } = useFeatureGate(
    'trello_xf_experiment_analytics_toggle',
  );

  const sendOperationalEvent = useCallback(
    (operationalEventFn: () => void) => {
      if (doSendOperationalEvent) {
        operationalEventFn();
      }
    },
    [doSendOperationalEvent],
  );

  const crossFlowNonPpmFreeSinglePlayerExperiment =
    useCrossFlowForNonPpmFreeSinglePlayerExperiment({
      source,
      workspaceId,
      boardId,
    });

  const crossFlowNonPpmMultiplayerExperiment =
    useCrossFlowForNonPpmFreeMultiplayer({
      source,
      workspaceId,
      boardId,
    });

  const crossFlowForNewUserNudgesExperiment = useCrossFlowForNewUserNudges({
    source,
    workspaceId,
    boardId,
  });

  const crossFlowForPaidWorkspaces = useCrossFlowForPaidWorkspaces({
    source,
    workspaceId,
    boardId,
  });

  const crossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment =
    useCrossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment({
      source,
      workspaceId,
      boardId,
    });

  const crossFlowForAdSequencerInternalExperiment =
    useCrossFlowForAdSequencerInternalExperiment({
      source,
      workspaceId,
      boardId,
    });

  const crossFlowWelcomeScreenTaskSelectionExperiment =
    useCrossFlowForWelcomeScreenTaskSelection({
      source,
    });

  const crossFlowForPaidUsersSpork = useCrossFlowForPaidUsersSpork({
    source,
    workspaceId,
    boardId,
  });

  const crossFlowForSinglePlayerSpork = useCrossFlowForSinglePlayerSpork({
    source,
    workspaceId,
    boardId,
  });
  const crossFlowForNewUsersSpork = useCrossFlowForNewUsersSpork({
    source,
    workspaceId,
    boardId,
  });

  const crossFlowForMultiplayerSpork = useCrossFlowForMultiplayerSpork({
    source,
    workspaceId,
    boardId,
  });

  const crossFlowForFreePpmSpork = useCrossFlowForFreePpmSpork({
    source,
    workspaceId,
    boardId,
  });

  const memberId = useMemberId();
  const { data: member } = useCrossFlowMemberFragment({
    from: { id: memberId },
    optimistic: false,
  });

  const trelloCrossFlow = {
    open: trelloCrossFlowOpen(crossFlow, source, [], hasValidAaSession, {
      workspaceId,
      boardId,
      isNewTrelloUser,
    }),
    getIsTargeted: () =>
      crossFlowNonPpmFreeSinglePlayerExperiment.getIsTargeted() ||
      crossFlowNonPpmMultiplayerExperiment.getIsTargeted() ||
      crossFlowForNewUserNudgesExperiment.getIsTargeted() ||
      crossFlowForPaidWorkspaces.getIsTargeted() ||
      crossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment.getIsTargeted() ||
      crossFlowWelcomeScreenTaskSelectionExperiment.getIsTargeted() ||
      crossFlowForPaidUsersSpork.getIsTargeted() ||
      crossFlowForAdSequencerInternalExperiment.getIsTargeted() ||
      crossFlowForSinglePlayerSpork.getIsTargeted() ||
      crossFlowForNewUsersSpork.getIsTargeted() ||
      crossFlowForMultiplayerSpork.getIsTargeted() ||
      crossFlowForFreePpmSpork.getIsTargeted(),
    getExperimentCohort: () => null,
  };

  // Return early if crossFlow is disabled (missing context provider, incompatible version...)
  if (!crossFlow.isEnabled) {
    return trelloCrossFlow;
  }

  // Check if the current source is eligible for the new user nudges experiment
  // This experiment can apply to unverified users, so we check it first
  if (crossFlowForNewUserNudgesExperiment.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForNewUserNudgesExperiment',
        },
      });
    });
    return crossFlowForNewUserNudgesExperiment;
  }

  // Check if the current source is eligible for the madshot v2 experiment
  // This experiment shows a questionnaire during the user onboarding flow, so the user might be unverified at this stage.
  if (crossFlowWelcomeScreenTaskSelectionExperiment.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForWelcomeScreenTaskSelectionExperiment',
        },
      });
    });
    return crossFlowWelcomeScreenTaskSelectionExperiment;
  }

  // Check if the current source is eligible for the paid users spork experiment
  if (crossFlowForPaidUsersSpork.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForPaidUsersSpork',
        },
      });
    });
    return crossFlowForPaidUsersSpork;
  }

  // Check if the current source is eligible for the singleplayer spork experiment
  if (crossFlowForSinglePlayerSpork.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForSinglePlayerSpork',
        },
      });
    });
    return crossFlowForSinglePlayerSpork;
  }

  // Check if the current source is eligible for the new users spork experiment
  if (crossFlowForNewUsersSpork.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForNewUsersSpork',
        },
      });
    });
    return crossFlowForNewUsersSpork;
  }
  // Check if the current source is eligible for the multiplayer spork experiment
  if (crossFlowForMultiplayerSpork.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForMultiplayerSpork',
        },
      });
    });
    return crossFlowForMultiplayerSpork;
  }

  // Check if the current source is eligible for the free ppm spork experiment
  if (crossFlowForFreePpmSpork.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForFreePpmSpork',
        },
      });
    });
    return crossFlowForFreePpmSpork;
  }

  // Disable crossFlow for users who have not yet confirmed their email address
  if (!member?.confirmed) {
    return trelloCrossFlow;
  }

  // Check if the current source is eligible for the non-ppm, free, single-player experiment
  if (crossFlowNonPpmFreeSinglePlayerExperiment.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowNonPpmFreeSinglePlayerExperiment',
        },
      });
    });
    return crossFlowNonPpmFreeSinglePlayerExperiment;
  }

  // Check if the current source is eligible for the paid workspaces experiment
  if (crossFlowForPaidWorkspaces.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForPaidWorkspaces',
        },
      });
    });
    return crossFlowForPaidWorkspaces;
  }

  // Check if the current source is eligible for the free ppm workspaces
  // with less than ten users experiment
  if (crossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment',
        },
      });
    });
    return crossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment;
  }

  // Check if the current source is eligible for the non-ppm, free, multiplayer experiment
  if (crossFlowNonPpmMultiplayerExperiment.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowNonPpmFreeMultiplayerExperiment',
        },
      });
    });
    return crossFlowNonPpmMultiplayerExperiment;
  }

  if (crossFlowForAdSequencerInternalExperiment.isEnrolled) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForAdSequencerInternalExperiment',
        },
      });
    });
    return crossFlowForAdSequencerInternalExperiment;
  }

  return trelloCrossFlow;
};
